/**
 * AP-37 Detailblatt O 9.1 — **Cockpit „Materialanfragen"**: Seitenkopf mit drei
 * Zählern, darunter die Reiter Material · Anfragen · Preisspiegel. Der Reiter
 * steht in der Adresse (`?tab=`), ein Vorfilter auf eine Kalkulation ebenso
 * (`?calculationId=`) — so landen Verweise aus der Kalkulation direkt.
 */
import { useCallback, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { PackageSearch } from 'lucide-react'
import { erfuelltRecht } from '@symphenia/shared-types'
import { useAuthStore } from '@/lib/auth.store'
import { pageWide } from '@/lib/form-styles'
import { Reiterleiste, type Reiter } from '@/components/ui/Reiterleiste'
import { MaterialReiter } from '../components/anfragen/MaterialReiter'
import { AnfragenReiter } from '../components/anfragen/AnfragenReiter'
import { AnfragenSenden, type ZuSenden } from '../components/anfragen/AnfragenSenden'
import { PreisspiegelReiter } from '../components/anfragen/PreisspiegelReiter'
import { PreisanfrageDialog } from '../components/anfragen/PreisanfrageDialog'
import type { CreateRequestsResult, MaterialListResponse, MaterialRow } from '../types/calculation-requests.types'

type ReiterKey = 'material' | 'anfragen' | 'preisspiegel'

export default function MaterialRequestsPage() {
  const [params, setParams] = useSearchParams()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  // 7.3: Der Preisspiegel braucht `calculation.cost.view` — ohne das Recht gibt es den Reiter nicht.
  const kosten = erfuelltRecht('calculation.cost.view', user?.permissions)
  const gewuenscht = (params.get('tab') as ReiterKey | null) ?? 'material'
  const reiter: ReiterKey = gewuenscht === 'preisspiegel' && !kosten ? 'material' : gewuenscht
  const calculationId = params.get('calculationId') ?? undefined
  const [zaehler, setZaehler] = useState<MaterialListResponse['counters'] | null>(null)
  const onZaehler = useCallback((z: MaterialListResponse['counters']) => setZaehler(z), [])
  const [anfragen, setAnfragen] = useState<MaterialRow[] | null>(null)
  const [ergebnis, setErgebnis] = useState<CreateRequestsResult | null>(null)
  const [neuLaden, setNeuLaden] = useState(0)
  const [zuSenden, setZuSenden] = useState<ZuSenden[] | null>(null)
  const [versandText, setVersandText] = useState<string | null>(null)

  const reiterListe: Reiter<ReiterKey>[] = [
    { key: 'material', label: 'Material' },
    { key: 'anfragen', label: 'Anfragen' },
    ...(kosten ? [{ key: 'preisspiegel' as const, label: 'Preisspiegel' }] : []),
  ]

  return (
    <div className={pageWide}>
      <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="flex items-center gap-2 text-3xl font-bold text-foreground">
            <PackageSearch className="h-7 w-7" aria-hidden /> Materialanfragen
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Material aller laufenden Kalkulationen — anfragen, Antworten erfassen, Preise übernehmen.
          </p>
        </div>
        {zaehler && (
          <div className="flex flex-wrap gap-3 text-sm">
            <Zaehler wert={zaehler.open} text="offen" />
            <Zaehler wert={zaehler.requested} text="angefragt ohne Antwort" zusatz={zaehler.overdue ? `${zaehler.overdue} überfällig` : undefined} />
            <Zaehler wert={zaehler.offersNotApplied} text="Angebote nicht übernommen" />
          </div>
        )}
      </div>

      {calculationId && (
        <div className="rounded-md bg-muted px-4 py-2 text-sm">
          Gefiltert auf eine Kalkulation —{' '}
          <Link
            to="?tab=material"
            className="text-primary hover:underline"
            onClick={(e) => {
              e.preventDefault()
              params.delete('calculationId')
              setParams(params)
            }}
          >
            alle zeigen
          </Link>
        </div>
      )}

      <Reiterleiste
        reiter={reiterListe}
        aktiv={reiter}
        onWahl={(key) => {
          params.set('tab', key)
          setParams(params)
        }}
      />

      {versandText && (
        <div className="flex items-start justify-between gap-2 rounded-md border border-border bg-muted px-4 py-2 text-sm">
          <span>{versandText}</span>
          <button type="button" className="text-muted-foreground hover:text-foreground" onClick={() => setVersandText(null)} aria-label="Hinweis schließen">
            ✕
          </button>
        </div>
      )}

      {ergebnis && (
        <div className="rounded-md border border-primary/30 bg-primary/5 px-4 py-3 text-sm">
          <div className="flex items-start justify-between gap-2">
            <div className="space-y-1">
              {ergebnis.created.length > 0 && (
                <div>
                  {ergebnis.created.length} Preisanfrage{ergebnis.created.length === 1 ? '' : 'n'} im Entwurf angelegt:{' '}
                  {ergebnis.created.map((c, i) => (
                    <span key={c.documentId}>
                      {i > 0 && ', '}
                      <Link to={`/correspondence/${c.documentId}`} className="text-primary hover:underline">
                        {c.documentNumber ?? 'Entwurf'} {c.supplierName}
                      </Link>
                    </span>
                  ))}
                </div>
              )}
              {ergebnis.created.flatMap((c) => c.skippedFiles.map((f) => ({ ...f, beleg: c.supplierName }))).map((f, i) => (
                <div key={i} className="text-xs text-amber-700 dark:text-amber-300">
                  {f.fileName} nicht mitgesendet ({f.beleg}): {f.reason === 'TYPE' ? 'Dateityp' : f.reason === 'TOO_MANY' ? 'mehr als 10 Dateien an der Zeile' : 'zu groß oder nicht lesbar'}
                </div>
              ))}
              {ergebnis.failed.map((f, i) => (
                <div key={i} className="text-destructive">
                  {f.supplierName}: fehlgeschlagen — {f.message}
                </div>
              ))}
            </div>
            <button type="button" className="text-muted-foreground hover:text-foreground" onClick={() => setErgebnis(null)} aria-label="Hinweis schließen">
              ✕
            </button>
          </div>
        </div>
      )}

      {reiter === 'material' && (
        <MaterialReiter calculationId={calculationId} onZaehler={onZaehler} onAnfragen={setAnfragen} neuLaden={neuLaden} alleMarkieren={params.get('markieren') === '1'} />
      )}
      {reiter === 'anfragen' && <AnfragenReiter calculationId={calculationId} neuLaden={neuLaden} />}
      {reiter === 'preisspiegel' && <PreisspiegelReiter calculationId={calculationId} neuLaden={neuLaden} />}

      {anfragen && (
        <PreisanfrageDialog
          zeilen={anfragen}
          onClose={() => setAnfragen(null)}
          sendenMoeglich
          onAngelegt={(r, senden) => {
            setAnfragen(null)
            // Nachtrag 3 (O-77): „Anlegen" mit genau einer Preisanfrage öffnet den Beleg. Schlug
            // etwas fehl oder blieb eine Datei unerwartet zurück, steht das hier — im Beleg sähe es niemand.
            const eine = !senden && r.created.length === 1 && r.failed.length === 0 ? r.created[0] : null
            if (eine && !eine.skippedFiles.some((f) => f.reason === 'FAILED')) {
              navigate(`/correspondence/${eine.documentId}`)
              return
            }
            setErgebnis(r)
            setVersandText(null)
            setNeuLaden((x) => x + 1)
            if (senden && r.created.length) setZuSenden(r.created.map((c) => ({ documentId: c.documentId, supplierName: c.supplierName })))
          }}
        />
      )}

      {zuSenden && (
        <AnfragenSenden
          belege={zuSenden}
          onFertig={(e) => {
            setZuSenden(null)
            setNeuLaden((x) => x + 1)
            setVersandText(
              [
                `${e.gesendet} Preisanfrage${e.gesendet === 1 ? '' : 'n'} gesendet`,
                e.uebersprungen.length ? `nicht gesendet: ${e.uebersprungen.join(', ')} — im Reiter Anfragen oder im Beleg nachholen` : '',
                ...e.fehler,
              ]
                .filter(Boolean)
                .join(' · '),
            )
          }}
        />
      )}
    </div>
  )
}

function Zaehler({ wert, text, zusatz }: { wert: number; text: string; zusatz?: string }) {
  return (
    <div className="rounded-md border border-border bg-card px-3 py-1.5">
      <span className="text-lg font-semibold tabular-nums text-foreground">{wert}</span> <span className="text-muted-foreground">{text}</span>
      {zusatz && <span className="ml-1 font-medium text-destructive">· {zusatz}</span>}
    </div>
  )
}
